import type { ControllerConstructor } from 'stimulus'

//-- Types --
export type ControllerModule = { default?: ControllerConstructor }
type LazyImportModule = () => Promise<ControllerModule>
export type LazyImportedModules = Record<string, LazyImportModule>

//-- Constants --
export const CONTROLLER_ATTRIBUTE = 'data-controller'
const CONTROLLER_FILENAME_REGEX =
  /^(?:.*?(?:controllers|components)\/|\.?\.\/)?(.+?)(?:(?:[_-]component)?[_-]controller\..+?)$/

//-- Given a file path, returns the controller name (identifier) --
// Example: "./controllers/foo_bar_controller.js" => "foo-bar"
// Example: "./controllers/booking/foo_bar_controller.js" => "booking--foo-bar"
export function controllerNameFromFilePath(filePath: string) {
  const name = (filePath.match(CONTROLLER_FILENAME_REGEX) || [])[1]
  if (name) return name.replace(/_/g, '-').replace(/\//g, '--')
  return
}

//-- Given a controller name (identifier), returns a string that can be used as a clsas name for that controller --
// Example 'builder--editor--node-tree' -> 'builder__editor__node_tree
export function controllerClassNameFromControllerName(controllerName: string) {
  return controllerName.replace(/-/g, '_')
}

//-- Given an element, returns array of controller names used by that element (i.e in the data-controller attribute) --
export function extractControllerNamesFrom(element: Element) {
  return (
    element
      .getAttribute(CONTROLLER_ATTRIBUTE)
      ?.split(/\s+/)
      ?.filter((content) => content.length) || []
  )
}

// Pair with `stimulus_lazy_controllers` (Ruby) — call this when the element
// becomes visible to mount the controllers (and trigger their JS import).
export function activateLazyControllers(element: HTMLElement) {
  const lazy = element.dataset.lazyControllers
  if (!lazy) return
  const existing = element.dataset.controller
  element.dataset.controller = existing ? `${existing} ${lazy}` : lazy
  delete element.dataset.lazyControllers
}

let loadingCount = 0
const controllerLoadingCounter = {
  get count() {
    return loadingCount
  },
  increment() {
    return ++loadingCount
  },
  decrement() {
    return --loadingCount
  },
}

function wrapWithLoadCounter(lazyImport: LazyImportModule) {
  // Cache the importer itself: Vite's preload work runs even for an already loaded module.
  let promise: Promise<ControllerModule> | null = null

  return () => {
    if (promise) return promise

    controllerLoadingCounter.increment()

    try {
      promise = lazyImport()
        .catch((error) => {
          promise = null
          throw error
        })
        .finally(() => {
          controllerLoadingCounter.decrement()
        })
    } catch (error) {
      controllerLoadingCounter.decrement()
      throw error
    }

    return promise
  }
}

export function pendingControllerCount(): number {
  return controllerLoadingCounter.count
}

// Include asynchronous registry lookup in the development readiness counter.
export function countPendingController<T>(work: Promise<T>): Promise<T> {
  if (!import.meta.env.DEV) return work
  controllerLoadingCounter.increment()
  return work.finally(() => controllerLoadingCounter.decrement())
}

//-- Converts the hash/object returned by vite's import.meta.glob (which is a hash of filePath to modules/module importer) to a hash of controller name to modules --
export function modulesByControllerName(modules: LazyImportedModules) {
  const result = {} as LazyImportedModules
  Object.entries(modules).forEach(([filePath, mod]) => {
    const controllerName = controllerNameFromFilePath(filePath)
    if (controllerName && mod) result[controllerName] = wrapWithLoadCounter(mod)
    else console.error(`Could not find controller name for ${filePath}`)
  })
  return result
}
